/**
 * Superset API Interfaces
 * Based on Apache Superset REST API
 */

// ============================================================================
// Column Types (from Superset coltypes)
// ============================================================================

export enum SupersetColumnType {
  NUMERIC = 0,
  STRING = 1,
  TEMPORAL = 2,
  BOOLEAN = 3,
}

// ============================================================================
// Filter Operators
// ============================================================================

export type SupersetFilterOperator =
  | '=='
  | '!='
  | '>'
  | '<'
  | '>='
  | '<='
  | 'like'
  | 'not like'
  | 'ilike'
  | 'not ilike'
  | 'in'
  | 'not in'
  | 'IS NULL'
  | 'IS NOT NULL'
  | 'TEMPORAL_RANGE';

// ============================================================================
// Chart List Response (GET /analytics/api/v1/chart/)
// ============================================================================

export interface IApiSupersetChartListItem {
  id: number;
  slice_name: string;
  slice_url: string;
  viz_type: string;
  datasource_id: number;
  datasource_type: string;
  datasource_name_text: string;
  description: string | null;
  cache_timeout: number;
  form_data: IApiSupersetFormData;
  params: string; // JSON string
  changed_by_name: string;
  changed_on_delta_humanized: string;
  changed_on_utc: string;
  dashboards: {
    id: number;
    dashboard_title: string;
  }[];
}

export interface IApiSupersetChartListResponse {
  count: number;
  ids: number[];
  result: IApiSupersetChartListItem[];
}

// ============================================================================
// Chart Explore Response (GET /analytics/api/v1/explore/?slice_id={id})
// ============================================================================

export interface IApiSupersetColumnInfo {
  column_name: string;
  verbose_name: string | null;
  type: string | null;
  type_generic: number | null;
  is_dttm: boolean;
  filterable: boolean;
  groupby: boolean;
  id: number;
  description: string | null;
  expression: string | null;
}

export interface IApiSupersetMetric {
  aggregate: string | null;
  column: IApiSupersetColumnInfo | null;
  expressionType: 'SQL' | 'SIMPLE';
  hasCustomLabel: boolean;
  label: string;
  optionName: string;
  sqlExpression: string | null;
}

export interface IApiSupersetFormData {
  datasource: string;
  viz_type: string;
  slice_id: number;
  query_mode: 'raw' | 'aggregate';
  groupby: string[];
  metrics: IApiSupersetMetric[];
  all_columns: (string | IApiSupersetSqlColumn)[];
  adhoc_filters: IApiSupersetAdhocFilter[];
  order_by_cols: string[];
  order_desc: boolean;
  row_limit: number | string;
  server_pagination: boolean;
  server_page_length: number;
  table_timestamp_format: string;
  column_config: Record<string, IApiSupersetColumnConfig>;
  temporal_columns_lookup: Record<string, boolean>;
}

export interface IApiSupersetSqlColumn {
  expressionType: 'SQL';
  label: string;
  sqlExpression: string;
}

export interface IApiSupersetColumnConfig {
  d3NumberFormat?: string;
  d3SmallNumberFormat?: string;
  truncateLongCells?: boolean;
}

export interface IApiSupersetAdhocFilter {
  clause: 'WHERE' | 'HAVING';
  comparator: string | string[] | null;
  expressionType: 'SIMPLE' | 'SQL';
  operator: SupersetFilterOperator;
  subject: string;
  sqlExpression?: string | null;
}

export interface IApiSupersetDataset {
  id: number;
  columns: IApiSupersetColumnInfo[];
  metrics: {
    id: number;
    metric_name: string;
    verbose_name: string;
    expression: string;
  }[];
  verbose_map: Record<string, string>;
  datasource_name: string;
  name: string;
  schema: string;
  table_name: string;
}

export interface IApiSupersetExploreResponse {
  result: {
    dataset: IApiSupersetDataset;
    form_data: IApiSupersetFormData;
    slice: {
      slice_id: number;
      slice_name: string;
      description: string | null;
      query_context: string; // JSON string containing IApiSupersetQueryContext
      form_data: IApiSupersetFormData;
    };
  };
}

// ============================================================================
// Chart Data Request/Response (POST /analytics/api/v1/chart/data)
// ============================================================================

export interface IApiSupersetQueryFilter {
  col: string;
  op: SupersetFilterOperator;
  val: string | string[] | number | number[] | boolean | null;
}

export interface IApiSupersetQuery {
  filters?: IApiSupersetQueryFilter[];
  extras?: {
    having?: string;
    where?: string;
  };
  columns?: (string | IApiSupersetSqlColumn)[];
  metrics?: IApiSupersetMetric[];
  orderby?: [string | IApiSupersetMetric | IApiSupersetSqlColumn, boolean][]; // [column/metric/sqlColumn, ascending]
  row_limit?: number;
  row_offset?: number;
  is_rowcount?: boolean;
  applied_time_extras?: Record<string, unknown>;
}

export interface IApiSupersetDataSource {
  id: number;
  type: 'table';
}

/** Datasource string format: "{id}__{type}" e.g. "1__table" */
export type SupersetDatasourceString = `${number}__${string}`;

export interface IApiSupersetChartDataRequest {
  /** Datasource object with id and type */
  datasource: IApiSupersetDataSource;
  queries: IApiSupersetQuery[];
  result_format: 'json' | 'csv';
  result_type: 'results' | 'full';
  form_data?: Partial<IApiSupersetFormData>;
}

export interface IApiSupersetChartDataResult {
  data: Record<string, unknown>[];
  colnames: string[];
  coltypes: SupersetColumnType[];
  rowcount: number;
  sql_rowcount?: number;
}

/** Result from rowcount query (is_rowcount: true) */
export interface IApiSupersetRowcountResult {
  data: { rowcount: number }[];
  colnames: string[];
  coltypes: SupersetColumnType[];
  rowcount: number;
}

export interface IApiSupersetChartDataResponse {
  result: IApiSupersetChartDataResult[];
}

// ============================================================================
// Parsed Query Context (from slice.query_context JSON string)
// ============================================================================

export interface IApiSupersetQueryContext {
  datasource: IApiSupersetDataSource;
  queries: IApiSupersetQuery[];
  form_data: IApiSupersetFormData;
  result_format: 'json' | 'csv';
  result_type: 'results' | 'full';
}
